import { StatCard } from "@/components/schedora/StatCard";
import { Calendar, DollarSign, Users, TrendingUp } from "lucide-react";
import { buildMonthLabel } from "@/lib/booking";
import { useOwnerSnapshot } from "@/context/AppContext";

export default function OwnerAnalytics() {
  const snapshot = useOwnerSnapshot(new Date().toISOString().slice(0, 10));
  const revenueMax = Math.max(...(snapshot?.monthlyRevenue.map((entry) => entry.revenue) ?? [1]));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground">Analytics</h1>
        <p className="text-sm text-muted-foreground">Performance is now calculated from your real in-app bookings</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Monthly Revenue" value={`$${(snapshot?.monthlyRevenue.at(-1)?.revenue ?? 0).toLocaleString()}`} icon={DollarSign} trend="Most recent calendar month" trendUp />
        <StatCard title="Total Bookings" value={snapshot?.appointments.filter((entry) => entry.status !== "cancelled").length ?? 0} icon={Calendar} trend="Confirmed, pending, and completed" trendUp />
        <StatCard title="Active Clients" value={snapshot?.clients.length ?? 0} icon={Users} trend="Clients with saved profiles" trendUp />
        <StatCard title="Avg per Client" value={`$${Math.round(((snapshot?.monthlyRevenue.reduce((sum, item) => sum + item.revenue, 0) ?? 0) / Math.max(snapshot?.clients.length ?? 1, 1))).toLocaleString()}`} icon={TrendingUp} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border bg-card p-6 shadow-soft">
          <h3 className="font-heading font-semibold mb-4">Revenue Trend</h3>
          <div className="space-y-3">
            {snapshot?.monthlyRevenue.map((month) => (
              <div key={month.month} className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-28">{buildMonthLabel(month.month)}</span>
                <div className="flex-1 h-6 rounded-full bg-secondary/50 overflow-hidden">
                  <div className="h-full rounded-full bg-primary/80 transition-all" style={{ width: `${(month.revenue / revenueMax) * 100}%` }} />
                </div>
                <span className="text-xs font-medium w-16 text-right">${month.revenue.toLocaleString()}</span>
              </div>
            ))}
            {!snapshot?.monthlyRevenue.length && (
              <p className="text-sm text-muted-foreground">Revenue data appears as soon as appointments exist.</p>
            )}
          </div>
        </div>

        <div className="rounded-xl border bg-card p-6 shadow-soft">
          <h3 className="font-heading font-semibold mb-4">Top Services</h3>
          <div className="space-y-3">
            {snapshot?.topServices.slice(0, 5).map((service, index) => (
              <div key={service.name} className="flex items-center justify-between py-2 border-b last:border-0">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-muted-foreground w-5">{index + 1}</span>
                  <div>
                    <p className="text-sm font-medium">{service.name}</p>
                    <p className="text-xs text-muted-foreground">{service.bookings} bookings</p>
                  </div>
                </div>
                <span className="text-sm font-semibold">${service.revenue.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
